
import { ADD_DONE_EXERCISE, DISCARD_EXERCISE, UNDISCARD_EXERCISE } from '../../mutations/MyExercice';
import { ExerciseModel, IExerciseModel } from './ExerciseModel';
import { FetchingState } from '../Fetching/FetchingState';
import { GET_EXERCISE, GET_MY_EXERCICES, GET_MY_EXERCISE_BY_ID } from '../../queries/Exercise';
import { captureException } from '../../../utils/sentry/sentry';
import { client, clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';
import { flow, getParent, getRoot, types } from 'mobx-state-tree';
import { hasError, isReady } from '../Fetching/updateStateHelper';

export interface ExerciseInput extends Partial<IExerciseModel> {
}



export const ExerciseStore = types
  .model('ExerciseStore', {
    exercise: types.array(types.maybeNull(ExerciseModel)),
    states: types.optional(types.array(FetchingState),[]),

  })
  .actions(self => {
    const loadExercise = (exercise: ExerciseInput, language?: string) => {

      const exerciseData = {
        ...filterDefinedFields(exercise,ExerciseModel.properties),
        language: language ? language : (getParent(self) as any).language.name.key };
      const index = self.exercise.findIndex(c => c && c._id === exercise._id);
      if (index === -1) {
        self.exercise?.push(exerciseData);
      } else {
        self.exercise[index] = { ...self.exercise[index], ...exerciseData };
      }

    };
    const fetchExercise = flow(function* (id: string) {
      updateState(id, 'pending');
      try {
        const { data }: any = yield clientFunction((getParent(self) as any).language.name.key)
          .query({ query: GET_EXERCISE, variables: { 'id': id } });
        if (data) {
          loadExercise({ ...data.exercice });
        }

        updateState(id, 'done');

      }
      catch (error) {
        updateState(id, 'error');

        captureException(error);

      }
    });

    const updateState = (id: string, newState: string)=> {
      const index = self.states.findIndex(s => s.id === id);
      if (index === -1) {
        self.states.push({ id, state: newState });
      } else {
        self.states[index].state = newState;
      }
    };

    const  setErrors = (id: [string]) => {
      id.forEach(i => setError(i));
    };

    const  setError = (id: string) => {
      const index = self.states.findIndex(s => s.id === id);
      if (index !== -1 && self.states[index].state === 'pending') {
        self.states[index].state = 'error';
      }
    };
    const fetchExerciseIfNotExist = flow(function* (id: string) {
      const index = self.exercise.findIndex(p => p && p._id === id);
      if (index === -1 || self.exercise[index].language !== (getParent(self) as any).language.name.key) {
        yield fetchExercise(id);
      }
    });
    const emptyMyExercice = () => {
      self.exercise = self.exercise.map(ex => { return ({ ...ex, myExerciceId: null }); });
    };
    const addToDashboard = (id: string, myExerciceId: string, image: string) => {

      if (self.exercise) {
        const index = self.exercise.findIndex(p => p && p._id === id);
        if (index !== -1) {
          self.exercise[index] = { ...self.exercise[index], completed: false, doneToday: false, myExerciceId: myExerciceId, image };
        }
      }
    };
    const discardExercise =  flow(function* (id: string) {
      const index = self.exercise.findIndex(p => p && p._id === id);
      if(index === -1) { return;}
      const exercise= self.exercise[index];
      if(!exercise) { return;}
      exercise.completed = true;
      exercise.doneToday = true;

      if(exercise?.myExerciceId) {
        try {
          yield client.mutate({
            mutation: DISCARD_EXERCISE, variables: {
              'id': exercise?.myExerciceId,
              action: 'complete'
            }, fetchPolicy: 'no-cache' });
        } catch(error) {
          captureException(error);
        }
      }

    });

    const undiscardExercise =  flow(function* (id: string) {
      const index = self.exercise.findIndex(p => p && p._id === id);
      if(index === -1) { return;}
      const exercise= self.exercise[index];
      self.exercise[index] = { ...exercise, completed: false};

      if(exercise?.myExerciceId) {
        try {
          yield client.mutate({
            mutation: UNDISCARD_EXERCISE, variables: {
              'id': exercise?.myExerciceId
            }, fetchPolicy: 'no-cache' });
        } catch(error) {
          captureException(error);
        }
      }

    });
    const setDoneToday = flow(function* (id: string) {
      const index = self.exercise.findIndex(p => p && p._id === id);
      if(index === -1) { return;}
      self.exercise[index] = { ...self.exercise[index], doneToday: true };
      const exercise= self.exercise[index];
      if(exercise?.myExerciceId) {
        try {
          yield client.mutate({ mutation: ADD_DONE_EXERCISE, variables: { 'id': exercise?.myExerciceId }, fetchPolicy: 'no-cache' });
        } catch(error) {
          captureException(error);
        }
      }

    }
    );


    const fetchMyExercise = flow(function* (language = 'en') {
      emptyMyExercice();
      const { data }: any = yield clientFunction(language).query({ query: GET_MY_EXERCICES , fetchPolicy: 'no-cache' });
      if (data) {
        const myExercices = data.retrieveActiveOrDoneTodayExercice;
        myExercices.forEach(e => {
          const myEx = {...e};
          const path = myEx.path._id;
          delete myEx.path;
          const exerciseData = myEx.exercice;
          delete myEx.exercice;
          const myExerciceId = myEx._id;

          const exIndex = self.exercise.findIndex(ex => ex && ex._id === e.exercice._id);
          if (exIndex !== -1) {
            self.exercise[exIndex] = { ...myEx,...exerciseData, path ,myExerciceId  };
          } else {
            loadExercise({ ...myEx, ...exerciseData, path ,myExerciceId }, language);
          }
        });
      }

    });
    const fetchMyExerciseById = flow(function* (id: string) {
      const language = (getRoot(self) as any).language.name.key || 'en';
      const { data }: any = yield clientFunction(language).query({ query: GET_MY_EXERCISE_BY_ID, variables: { 'id': id } });
      if (data) {
        const myEx = {...data.retrieveMyExerciceById};
        const path = myEx.path._id;
        delete myEx.path;
        const exerciseData = myEx.exercice;
        delete myEx.exercice;
        const myExerciceId = myEx._id;

        const exIndex = self.exercise.findIndex(ex => ex && ex._id === exerciseData._id);
        if (exIndex !== -1) {
          self.exercise[exIndex] = { ...myEx,...exerciseData, path ,myExerciceId  };
        } else {
          loadExercise({ ...myEx, ...exerciseData, path ,myExerciceId }, language);
        }
      }
    });
    const clear = () => {
      self.exercise = [];
      self.states = [];
    };
    return {
      emptyMyExercice,
      fetchMyExercise,
      loadExercise,
      fetchExercise,
      fetchExerciseIfNotExist,
      fetchMyExerciseById,
      addToDashboard,
      setError,
      setErrors,
      discardExercise,
      setDoneToday,
      undiscardExercise,
      clear
    };
  })
  .views(self => ({
    getExercise(id: string) {
      const index = self.exercise.findIndex(p => p && p._id === id);
      if (index === -1) {
        return null;
      } else {
        return self.exercise[index];
      }

    },
    get getDashboard() {
      return self.exercise.filter(e => e && (!e.completed || e.doneToday) && e.myExerciceId);
    },
    get allExercisesFinishedToday() {
      const indashboardExercise = self.exercise.filter(e => e && !e.completed && e.myExerciceId);
      return indashboardExercise.every(e => e?.doneToday);
    },
    hasError(id: string) {
      return hasError(self, id);
    },
    hasErrorPlural(id: string[]) {
      return id.every(i => hasError(self, i));
    },
    isReady(id: string) {
      return isReady(self, id);
    },
    isReadyPlural(id: string[]) {
      return id.every(i => isReady(self, i));
    },
  }));
